import { EntityDto, PagedResultDto } from '../dto';
import http from '../httpService';
import {
  CreateInventory,
  ImportInventoryResult,
  ImportInventoryRow,
  Inventory,
  InventoryLookup,
  InventoryMovement,
  PagedInventoryMovementRequest,
  PagedInventoryRequest,
  UpdateInventory,
  CreateSupplier,
  Supplier,
  PagedSupplierRequest,
  SupplierLookup,
  UpdateSupplier,
  InventoryCategory,
  CreateInventoryCategory,
  UpdateInventoryCategory,
} from './dto';

class InventoryService {
  // Inventory CRUD
  public async getAll(request: PagedInventoryRequest): Promise<PagedResultDto<Inventory>> {
    const result = await http.post('api/services/app/Inventory/GetAll', request);
    return result.data.result;
  }

  public async get(entityDto: EntityDto<string>): Promise<Inventory> {
    const result = await http.get('api/services/app/Inventory/Get', { params: entityDto });
    return result.data.result;
  }

  public async create(request: CreateInventory): Promise<Inventory> {
    const result = await http.post('api/services/app/Inventory/Create', request);
    return result.data.result;
  }

  public async update(request: UpdateInventory): Promise<Inventory> {
    const result = await http.put('api/services/app/Inventory/Update', request);
    return result.data.result;
  }

  public async delete(entityDto: EntityDto<string>): Promise<void> {
    await http.delete('api/services/app/Inventory/Delete', { params: entityDto });
  }

  public async createBulk(request: {
    supplierId?: string;
    supplierInvoiceNumber?: string;
    documentId?: string;
    orderId?: string;
    items: CreateInventory[];
  }): Promise<Inventory[]> {
    const result = await http.post('api/services/app/Inventory/CreateBulk', request);
    return result.data.result;
  }

  public async import(rows: ImportInventoryRow[]): Promise<ImportInventoryResult> {
    const result = await http.post(
      'api/services/app/Inventory/Import',
      { rows },
      { timeout: 120000 }
    );
    return result.data.result;
  }

  public async lookup(search: string): Promise<InventoryLookup[]> {
    const result = await http.get('api/services/app/Inventory/GetLookup', { params: { search } });
    return result.data.result;
  }

  public async getAllSuppliers(request: PagedSupplierRequest): Promise<PagedResultDto<Supplier>> {
    const result = await http.get('api/services/app/Supplier/GetAll', { params: request });
    return result.data.result;
  }

  public async getSupplier(entityDto: EntityDto<string>): Promise<Supplier> {
    const result = await http.get('api/services/app/Supplier/Get', { params: entityDto });
    return result.data.result;
  }

  public async createSupplier(request: CreateSupplier): Promise<Supplier> {
    const result = await http.post('api/services/app/Supplier/Create', request);
    return result.data.result;
  }

  public async updateSupplier(request: UpdateSupplier): Promise<Supplier> {
    const result = await http.put('api/services/app/Supplier/Update', request);
    return result.data.result;
  }

  public async deleteSupplier(entityDto: EntityDto<string>): Promise<void> {
    await http.delete('api/services/app/Supplier/Delete', { params: entityDto });
  }

  public async supplierLookup(search: string): Promise<SupplierLookup[]> {
    const result = await http.get('api/services/app/Supplier/GetLookup', { params: { search } });
    return result.data.result;
  }

  public async findOrCreateSupplier(request: CreateSupplier): Promise<Supplier> {
    const result = await http.post('api/services/app/Supplier/FindOrCreate', request);
    return result.data.result;
  }

  public async getAllCategories(): Promise<PagedResultDto<InventoryCategory>> {
    const result = await http.get('api/services/app/InventoryCategory/GetAll', {
      params: { maxResultCount: 100, skipCount: 0 },
    });
    return result.data.result;
  }

  public async createCategory(request: CreateInventoryCategory): Promise<InventoryCategory> {
    const result = await http.post('api/services/app/InventoryCategory/Create', request);
    return result.data.result;
  }

  public async updateCategory(request: UpdateInventoryCategory): Promise<InventoryCategory> {
    const result = await http.put('api/services/app/InventoryCategory/Update', request);
    return result.data.result;
  }

  public async deleteCategory(entityDto: EntityDto<string>): Promise<void> {
    await http.delete('api/services/app/InventoryCategory/Delete', { params: entityDto });
  }

  // Invoice upload + AI parse
  public async uploadInvoice(request: {
    name: string;
    content: string;
    contentType: string;
  }): Promise<any> {
    const result = await http.post('api/services/app/Inventory/UploadInvoice', request, {
      timeout: 120000,
    });
    return result.data.result;
  }

  public async parseInvoice(request: { name: string; content: string; contentType: string; categoryNames?: string[] }): Promise<any> {
    const result = await http.post('api/services/app/Inventory/ParseInvoice', request, {
      timeout: 120000,
    });
    return result.data.result;
  }

  // Capture session (QR code mobile upload)
  public async createCaptureSession(): Promise<{ id: string; token: string }> {
    const result = await http.post('api/services/app/Inventory/CreateCaptureSession');
    return result.data.result;
  }

  public async getCaptureSession(token: string): Promise<{ id: string; token: string; documentId: string | null; isCompleted: boolean }> {
    const result = await http.get('api/services/app/Inventory/GetCaptureSession', { params: { token } });
    return result.data.result;
  }

  public async uploadCapture(token: string, request: { name: string; content: string; contentType: string }): Promise<any> {
    const result = await http.post('api/services/app/Inventory/UploadCapture', request, { params: { token } });
    return result.data.result;
  }

  public async getAllMovements(request: PagedInventoryMovementRequest): Promise<PagedResultDto<InventoryMovement>> {
    const result = await http.post('api/services/app/InventoryMovement/GetAll', request);
    return result.data.result;
  }

  // Create extra from inventory
  public async createExtraFromInventory(request: {
    inventoryId: string;
    serviceOrderId: string;
    quantity: number;
    sellPrice?: number;
  }): Promise<any> {
    const result = await http.post(
      'api/services/app/ServiceOrderExtra/CreateFromInventory',
      request
    );
    return result.data.result;
  }
}

export default new InventoryService();
